import type { OwnerDisbursement } from "@/api/SolarCloudApi";

export type QuarterNumber = 1 | 2 | 3 | 4;

export type SydneyQuarter = {
  year: number;
  quarter: QuarterNumber;
  label: string;
  dateRange: string;
  sortKey: number;
  startMonth: number;
  endMonth: number;
};

export type SydneyQuarterDisbursementTotal = {
  quarter: SydneyQuarter;
  amount: number;
};

const quarterDateRanges = ["Jan - Mar", "Apr - Jun", "Jul - Sep", "Oct - Dec"];

export function getSydneyQuarter(date: Date): SydneyQuarter {
  const { year, month } = getSydneyYearMonth(date);
  const quarter = (Math.floor((month - 1) / 3) + 1) as QuarterNumber;

  return {
    year,
    quarter,
    label: `Q${quarter} ${year}`,
    dateRange: quarterDateRanges[quarter - 1],
    sortKey: year * 10 + quarter,
    startMonth: (quarter - 1) * 3 + 1,
    endMonth: quarter * 3,
  };
}

function getSydneyYearMonth(date: Date): { year: number; month: number } {
  const dateParts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "numeric",
  }).formatToParts(date);
  const year = Number(dateParts.find((part) => part.type === "year")?.value);
  const month = Number(dateParts.find((part) => part.type === "month")?.value);

  return { year, month };
}

export function getNextCreditWindow(date: Date = new Date()): {
  startMonth: number;
  endMonth: number;
  year: number;
} {
  const { year, month } = getSydneyYearMonth(date);

  const isFebToApr = month >= 2 && month <= 4;
  const isMayToJul = month >= 5 && month <= 7;
  const isAugToOct = month >= 8 && month <= 10;

  if (isFebToApr) {
    return { startMonth: 4, endMonth: 5, year };
  }

  if (isMayToJul) {
    return { startMonth: 7, endMonth: 8, year };
  }

  if (isAugToOct) {
    return { startMonth: 10, endMonth: 11, year };
  }

  const targetYear = month === 1 ? year : year + 1;
  return { startMonth: 1, endMonth: 2, year: targetYear };
}

export function calculateNextCreditDate(date: Date = new Date()): Date {
  const { startMonth, year } = getNextCreditWindow(date);
  return new Date(Date.UTC(year, startMonth - 1, 1));
}

export function formatNextCreditDate(
  date: Date = new Date(),
  monthFormat: "long" | "short" = "long"
): string {
  const { startMonth, endMonth, year } = getNextCreditWindow(date);

  const formatter = new Intl.DateTimeFormat("en-AU", {
    month: monthFormat,
    timeZone: "UTC",
  });

  const firstMonthName = formatter.format(
    new Date(Date.UTC(year, startMonth - 1, 1))
  );
  const secondMonthName = formatter.format(
    new Date(Date.UTC(year, endMonth - 1, 1))
  );

  return `${firstMonthName}-${secondMonthName} ${year}`;
}

export function filterEligibleDisbursements(
  disbursements: OwnerDisbursement[]
): OwnerDisbursement[] {
  return disbursements.filter((disbursement) => {
    const state = (disbursement.state ?? "").trim().toLowerCase();
    const type = (disbursement.type ?? "").trim().toLowerCase();
    const currency = (disbursement.currency ?? "").trim().toUpperCase();

    return (
      state === "disbursed" &&
      type === "powergeneration" &&
      currency === "AUD" &&
      (disbursement.amount ?? 0) > 0
    );
  });
}

export function groupEligibleDisbursementsBySydneyQuarter(
  disbursements: OwnerDisbursement[]
): SydneyQuarterDisbursementTotal[] {
  const totals = new Map<number, SydneyQuarterDisbursementTotal>();

  for (const disbursement of filterEligibleDisbursements(disbursements)) {
    const date =
      parseDate(disbursement.start) ??
      parseDate(disbursement.end) ??
      parseDate(disbursement.timestamp);

    if (!date) continue;

    const quarter = getSydneyQuarter(date);
    const existingTotal = totals.get(quarter.sortKey);

    if (existingTotal) {
      existingTotal.amount += disbursement.amount!;
    } else {
      totals.set(quarter.sortKey, {
        quarter,
        amount: disbursement.amount!,
      });
    }
  }

  return Array.from(totals.values()).sort(
    (firstTotal, secondTotal) =>
      firstTotal.quarter.sortKey - secondTotal.quarter.sortKey
  );
}

function parseDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
